/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface UnitEMSystemMappingProps {
  id: string;
  resourceId: string;
  sendGps: boolean;
  divisionName: string | null;
  unitId: string;
  companyId: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class UnitEMSystemMappingRecord extends Record<UnitEMSystemMappingProps>({
  id: null as any,
  resourceId: null as any,
  sendGps: null as any,
  divisionName: null as any,
  unitId: null as any,
  companyId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    resourceId: string;
    sendGps: boolean;
    divisionName: string | null;
    unitId: string;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): UnitEMSystemMappingRecord {
    if (json == null) {
      throw new Error('UnitEMSystemMappingRecord.fromJS: parameter cannot be null');
    }
    return new UnitEMSystemMappingRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'UnitEMSystemMappingJson.id',
      ),
      resourceId: getFromOrThrow(json, (j) => j.resourceId, 'UnitEMSystemMappingJson.resourceId'),
      sendGps: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'sendGps', false),
        'UnitEMSystemMappingJson.sendGps',
      ),
      divisionName: json.divisionName,
      unitId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'unitId', '00000000-0000-0000-0000-000000000000'),
        'UnitEMSystemMappingJson.unitId',
      ),
      companyId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
        'UnitEMSystemMappingJson.companyId',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    resourceId: string;
    sendGps: boolean;
    divisionName: string | null;
    unitId: string;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [UnitEMSystemMappingRecord, Error[]] {
    if (json == null) {
      throw new Error('UnitEMSystemMappingRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new UnitEMSystemMappingRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'UnitEMSystemMappingJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      resourceId: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.resourceId, 'UnitEMSystemMappingJson.resourceId'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      sendGps: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'sendGps', false),
            'UnitEMSystemMappingJson.sendGps',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      divisionName: tryOrDefault(
        () => json.divisionName,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      unitId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'unitId', '00000000-0000-0000-0000-000000000000'),
            'UnitEMSystemMappingJson.unitId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
            'UnitEMSystemMappingJson.companyId',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: UnitEMSystemMappingProps) {
    super(props);
    if (props == null) {
      throw new Error('UnitEMSystemMappingRecord constructor parameter cannot be null');
    }
  }
}

export abstract class UnitEMSystemMappingViewModelBase implements UnitEMSystemMappingProps {
  protected readonly _props: UnitEMSystemMappingProps;
  private _json?: UnitEMSystemMappingProps;

  get id(): string {
    return this._props.id;
  }
  get resourceId(): string {
    return this._props.resourceId;
  }
  get sendGps(): boolean {
    return this._props.sendGps;
  }
  get divisionName(): string | null {
    return this._props.divisionName;
  }
  get unitId(): string {
    return this._props.unitId;
  }
  get companyId(): string {
    return this._props.companyId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  constructor(props: UnitEMSystemMappingProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        resourceId: this._props.resourceId,
        sendGps: this._props.sendGps,
        divisionName: this._props.divisionName,
        unitId: this._props.unitId,
        companyId: this._props.companyId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllUnitEMSystemMappingEntities = (state: AppState) =>
  state.entityStores.unitEMSystemMapping.entities;

export const selectUnitEMSystemMappingById = (id: string) => (state: AppState) =>
  state.entityStores.unitEMSystemMapping.entities.get(id);

export const selectUnitEMSystemMappingMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.unitEMSystemMapping.meta.get(id);

export const selectUnitEMSystemMappingAndMetadataById = (id: string) =>
  createSelector(
    selectUnitEMSystemMappingById(id),
    selectUnitEMSystemMappingMetadataById(id),
    (unitEMSystemMapping, meta) => ({ unitEMSystemMapping, meta }),
  );

export function selectUnitEMSystemMappingEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllUnitEMSystemMappingEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, UnitEMSystemMappingRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectUnitEMSystemMappingEntitiesByResourceId(resourceId: string) {
  return createSelector(selectAllUnitEMSystemMappingEntities, (entities) =>
    entities.filter((e) => e.resourceId === resourceId).valueSeq(),
  );
}

export function selectUnitEMSystemMappingEntitiesBySendGps(sendGps: boolean) {
  return createSelector(selectAllUnitEMSystemMappingEntities, (entities) =>
    entities.filter((e) => e.sendGps === sendGps).valueSeq(),
  );
}

export function selectUnitEMSystemMappingEntitiesByDivisionName(divisionName: string) {
  return createSelector(selectAllUnitEMSystemMappingEntities, (entities) =>
    entities.filter((e) => e.divisionName === divisionName).valueSeq(),
  );
}

export function selectUnitEMSystemMappingEntitiesByUnitId(unitId: string) {
  return createSelector(selectAllUnitEMSystemMappingEntities, (entities) =>
    entities.filter((e) => e.unitId === unitId).valueSeq(),
  );
}

export function selectUnitEMSystemMappingEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllUnitEMSystemMappingEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}
//#endregion Selectors
